import React, { useState, useEffect, useContext } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import "../../../assests/css/customer/CustomerFeedback/CustomerFeedback.css";

const FEATURE_CONFIG = {
  tryOn: {
    title: "Try-On Feedback",
    question: "How was your virtual try-on experience?",
    placeholder: "Tell us what worked well or what could be better...",
  },
  customization: {
    title: "Customization Feedback",
    question: "How was your customization experience?",
    placeholder: "Tell us what worked well or what could be better...",
  },
};

// Star display — reusable for both modal and review cards
const StarRow = ({ rating, size = "modal" }) => {
  return (
    <div className="customer-feedback-review-stars">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`customer-feedback-review-star ${
            star <= rating ? "customer-feedback-review-star-active" : ""
          }`}
          style={{ fontSize: size === "modal" ? "38px" : "1rem" }}
        >
          &#9733;
        </span>
      ))}
    </div>
  );
};

// ─── Helper: get logged-in customer from context, fallback to localStorage ──
const getLoggedInCustomer = (contextCustomer) => {
  if (contextCustomer && contextCustomer.id) return contextCustomer;

  try {
    const stored = JSON.parse(localStorage.getItem("customerData") || "null");
    if (stored && stored.id) return stored;
  } catch (e) {
    console.error("Failed to parse stored customer data:", e);
  }

  return null;
};

// ─── Feedback popup modal ───────────────────────────────────────────────────
const CustomerFeedback = ({ show, onClose, featureType = "tryOn", onSubmit }) => {
  const { baseUrl, customer } = useContext(AppContext);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const config = FEATURE_CONFIG[featureType] || FEATURE_CONFIG.tryOn;

  const resetForm = () => {
    setRating(0);
    setHoverRating(0);
    setComment("");
    setError("");
  };

  const submitFeedback = () => {
    if (rating === 0 || isSubmitting) return;

    const loggedInCustomer = getLoggedInCustomer(customer);

    if (!loggedInCustomer) {
      setError("Please log in to submit your feedback.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    axios
      .post(`${baseUrl}/customer/feedback/submit`, {
        Mlcustomerauth_id: loggedInCustomer.id,
        customerName: loggedInCustomer.name,
        customerEmail: loggedInCustomer.email,
        featureType,
        rating,
        comment,
      })
      .then((response) => {
        if (onSubmit) onSubmit(response.data);
        resetForm();
        onClose();
      })
      .catch((err) => {
        const serverMessage =
          err.response?.data?.message ||
          (err.response?.data?.errors &&
            Object.values(err.response.data.errors).flat().join(" ")) ||
          "Something went wrong. Please try again.";
        console.error("Feedback submit failed:", err.response?.status, err.response?.data || err.message);
        setError(serverMessage);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="customer-feedback-dialog"
      contentClassName="customer-feedback-content"
    >
      {/* Header */}
      <div className="customer-feedback-header">
        <h2 className="customer-feedback-title">{config.title}</h2>
        <button
          type="button"
          className="customer-feedback-close-btn"
          onClick={handleClose}
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      <div className="customer-feedback-divider" />

      {/* Body */}
      <div className="customer-feedback-body">
        <p className="customer-feedback-question">{config.question}</p>

        {/* Interactive stars */}
        <div className="customer-feedback-stars">
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              className={`customer-feedback-star ${
                star <= (hoverRating || rating) ? "customer-feedback-star-active" : ""
              }`}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
            >
              &#9733;
            </span>
          ))}
        </div>

        <p className="customer-feedback-hint">Tap a star to rate your experience</p>

        <Form.Group className="customer-feedback-form-group">
          <Form.Label className="customer-feedback-label">
            Anything else you'd like to share?
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            className="customer-feedback-textarea"
            placeholder={config.placeholder}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Form.Group>

        {error && <p className="customer-feedback-error">{error}</p>}

        <Button
          className="customer-feedback-submit-btn"
          onClick={submitFeedback}
          disabled={rating === 0 || isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit Feedback"}
        </Button>

        <p className="customer-feedback-footer-text">
          Your feedback helps us improve our virtual fitting technology.
        </p>
      </div>
    </Modal>
  );
};

// ─── Reviews list with admin reply support ──────────────────────────────────
// featureType : "tryOn" | "customization"
// refreshKey  : koi bhi value jo submit k baad change ho — list re-fetch karti hy
export const FeedbackReviews = ({ featureType, refreshKey }) => {
  const { baseUrl } = useContext(AppContext);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError("");

    axios
      .post(`${baseUrl}/customer/feedback/getFeedback`, { featureType })
      .then((response) => {
        if (isMounted) {
          setReviews(Array.isArray(response.data) ? response.data : []);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch feedback:", err.response?.status, err.response?.data || err.message);
        if (isMounted) setError("Could not load reviews.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [featureType, refreshKey, baseUrl]);

  if (loading || error || reviews.length === 0) return null;

  const averageRating =
    reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;

  const sectionTitle =
    featureType === "customization" ? "Customization Reviews" : "Try-On Reviews";

  return (
    <div className="customer-feedback-reviews-section">
      {/* Section header */}
      <div className="customer-feedback-reviews-header">
        <h3 className="customer-feedback-reviews-title">{sectionTitle}</h3>
        <div className="customer-feedback-reviews-average">
          <span className="customer-feedback-reviews-average-star">&#9733;</span>
          <span>{averageRating.toFixed(1)}</span>
          <span className="customer-feedback-reviews-count">
            ({reviews.length} {reviews.length === 1 ? "review" : "reviews"})
          </span>
        </div>
      </div>

      {/* Cards */}
      <div className="customer-feedback-reviews-list">
        {reviews.map((review) => (
          <div key={review.id} className="customer-feedback-review-card">
            {/* Top row: avatar + name/date + stars */}
            <div className="customer-feedback-review-top">
              <div className="customer-feedback-review-avatar">
                {(review.customerName || "U").charAt(0).toUpperCase()}
              </div>

              <div className="customer-feedback-review-name-date">
                <span className="customer-feedback-review-name">
                  {review.customerName || "Customer"}
                </span>
                <span className="customer-feedback-review-date">
                  {(review.created_at || "").slice(0, 10)}
                </span>
              </div>

              <div className="customer-feedback-review-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`customer-feedback-review-star ${
                      star <= review.rating ? "customer-feedback-review-star-active" : ""
                    }`}
                  >
                    &#9733;
                  </span>
                ))}
              </div>
            </div>

            {/* Customer comment */}
            {review.comment ? (
              <p className="customer-feedback-review-comment">{review.comment}</p>
            ) : null}

            {/* Admin reply — backend field is snake_case: admin_reply */}
            {review.admin_reply ? (
              <div className="customer-feedback-admin-reply">
                <div className="customer-feedback-admin-reply-label">
                  {/* checkmark icon */}
                  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="8" r="8" fill="#3a7d44" />
                    <path
                      d="M4.5 8.5L6.8 11L11.5 5.5"
                      stroke="#fff"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Store Response
                </div>
                <p className="customer-feedback-admin-reply-text">{review.admin_reply}</p>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomerFeedback;